import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker from 'components/CustomInputs/DatePicker';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { FormStepComponentProps } from 'components/Forms/MultiStepForm';
import { AppContext } from 'context';
import {
  useActiveContractToDate,
  useCharacterizationsWithFilter,
  useClientsWithActiveContractToDate,
  useStreamsWithClientAndActiveContractToDate,
} from 'hooks';
import { CharacterizationForm } from 'types';
import { useLatestClosureDate } from 'utils/closureHooks';
import { isValidDate } from 'utils/date';
import { CharacterizationFormContext } from 'pages/Characterizations/CharacterizationCreate';
import { characterizationFormSchema } from './Step1.yup';
import { notify } from '../../Notifier';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  fieldLine: {
    marginTop: '8px',

    '& > div': {
      minWidth: '470px',
    },

    '& span': {
      fontSize: 18,
      marginBottom: '5px',
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: '214px',
    },
  },
  materialTable: {
    width: '470px',
  },
  materialWeightCell: {
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    width: '50%',
    padding: '1px 13px',
  },
  materialNameCell: {
    padding: '6.5px 22px',
  },
  materialLabel: {
    fontSize: 'inherit',
    color: 'inherit',
  },
  totalWeightCell: {
    padding: '7.5px 13px',
  },
  totalWeightField: {
    width: '171px',
    textAlign: 'right',
    padding: 0,
  },
  headerCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: '11px 22px',
  },
});

const Step1: React.FC<FormStepComponentProps<CharacterizationForm, CharacterizationFormContext>> = ({
  onSubmit,
  formName,
  previousStepData,
  setFormContext,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'characterizationForm' });

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  const [closureMonth, twoMonthAfterClosureDate] = useLatestClosureDate(sortingCenter);
  const currentId = previousStepData.id;

  const schema = useMemo(
    () => characterizationFormSchema(t, closureMonth, twoMonthAfterClosureDate),
    [t, closureMonth, twoMonthAfterClosureDate],
  );

  const { control, handleSubmit, watch, setValue, trigger, setError, clearErrors } = useForm<CharacterizationForm>({
    mode: 'onBlur',
    defaultValues: previousStepData,
    resolver: yupResolver(schema),
  });

  const watchSamplingDate = watch('samplingDate', previousStepData.samplingDate);
  const watchClient = watch('client', previousStepData.client);

  const activeContractsToDate = useActiveContractToDate(watchSamplingDate);
  const clientChoices = useClientsWithActiveContractToDate(activeContractsToDate);
  const streamChoices = useStreamsWithClientAndActiveContractToDate(watchClient, activeContractsToDate);

  const samplingDate = watch('samplingDate');
  const client = watch('client');
  const stream = watch('stream');

  const dateFilter = isValidDate(samplingDate)
    ? { client, stream, startDate: samplingDate, endDate: samplingDate }
    : null;

  const [duplicates] = useCharacterizationsWithFilter(null, undefined, dateFilter);
  const duplicatesFiltered = useMemo(
    () => duplicates?.filter(d => d['@id'] !== currentId) ?? [],
    [duplicates, currentId],
  );
  useEffect(() => {
    if (client && stream && isValidDate(samplingDate)) {
      if (duplicatesFiltered?.length === 0) {
        clearErrors(['samplingDate', 'stream', 'client']);
      }
    }
  }, [duplicatesFiltered, client, stream, samplingDate, clearErrors]);

  const onLocalSubmit: SubmitHandler<CharacterizationForm> = data => {
    if (client && stream && isValidDate(samplingDate) && duplicatesFiltered?.length! > 0) {
      setError('samplingDate', {});
      setError('stream', {});
      setError('client', {});
      notify({
        message: t('validation.duplicateSamplingStream'),
        severity: 'error',
      });
      return;
    }
    onSubmit(data);
  };

  return (
    <form id={formName} onSubmit={handleSubmit(onLocalSubmit)} className={classes.form}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="samplingDate"
            control={control}
            render={({ field: { ref, onChange, onBlur, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="samplingDate"
                  label={t('samplingDate')}
                  invalidDateMessage={t('validation.date.invalid')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{
                    required: true,
                  }}
                  onChange={e => {
                    onChange(e);
                    setValue('client', '');
                    setValue('stream', '');
                    clearErrors(['samplingDate', 'stream', 'client']);
                  }}
                  onBlur={() => {
                    trigger(['analyseDate', 'samplingDate']);
                  }}
                  onAccept={() => {
                    trigger(['analyseDate', 'samplingDate']);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
          <Controller
            name="analyseDate"
            control={control}
            render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="analyseDate"
                  label={t('analyseDate')}
                  invalidDateMessage={t('validation.date.invalid')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  onBlur={() => {
                    trigger('analyseDate');
                  }}
                  onAccept={() => {
                    trigger(['analyseDate', 'samplingDate']);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="client"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <SelectWithErrorField
              id="client"
              choices={clientChoices}
              choiceDisplayKey="shortName"
              selectLabel={t('client')}
              InputLabelProps={{
                required: true,
              }}
              disabled={null === watchSamplingDate || !isValidDate(watchSamplingDate)}
              error={fieldState.invalid}
              errorMessage={fieldState.error?.message}
              inputRef={ref}
              {...inputProps}
              onChange={e => {
                onChange(e);
                setValue('stream', '');
                clearErrors(['samplingDate', 'stream', 'client']);
                const clientId = e.target.value;
                setFormContext(context => ({
                  ...context,
                  client: clientChoices.find(c => c['@id'] === clientId),
                }));
              }}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="stream"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <SelectWithErrorField
              choices={streamChoices}
              choiceDisplayKey="name"
              selectLabel={t('stream')}
              InputLabelProps={{
                required: true,
              }}
              disabled={null === watchSamplingDate || '' === watchClient}
              error={fieldState.invalid}
              errorMessage={fieldState.error?.message}
              inputRef={ref}
              {...inputProps}
              onChange={e => {
                onChange(e);
                setValue('characterizationMaterials', []);
                const streamId = e.target.value;
                setFormContext(context => ({
                  ...context,
                  stream: streamChoices.find(s => s['@id'] === streamId),
                }));
              }}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <FormControl>
          <Controller
            name="weight"
            control={control}
            render={({ field: { ref, ...rest }, fieldState }) => (
              <WeightInputField
                id="weight"
                label={t('weight')}
                inputRef={ref}
                InputLabelProps={{
                  required: true,
                }}
                variant="outlined"
                defaultValue={previousStepData.weight ?? ''}
                unit="kg"
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
                {...rest}
              />
            )}
          />
        </FormControl>
      </div>
    </form>
  );
};

export default Step1;
